<template>
    <div id="meta-insights-channel-detail">
        <AppBreadCrumb
            :paths="[{ title: 'Meta Insights Channel Detail' }]"
            :select="true"
            class="visitor-detail-bread-crumb"
        />
        <div class="wrapper" v-if="channel">
            <div class="toolbar">
                <div class="left">
                    <IconButton :on-click="() => $router.push('/integrations/meta-insights')">
                        <span class="material-icons">chevron_left</span>
                    </IconButton>
                    <div class="icon-container">
                        <MetaIcon />
                    </div>
                    <div class="name">
                        {{ $t("metaInsights.channelTitle") }} : {{ channel.name }}
                        <span class="ad-account" v-if="adAccountName">— {{ adAccountName }}</span>
                    </div>
                </div>
                <div class="right">
                    <a-button type="danger" @click="deleteModal = true">
                        {{ $t("whatsappChannel.deleteChannel") }}
                    </a-button>
                </div>
            </div>

            <div class="channel-content">
                <SidebarChild
                    :items="menuItems"
                    :selected-item="currentTab"
                    :focusActive="true"
                    @selected="(i) => (currentTab = i.key)"
                />
                <div class="page-content">
                    <Overview
                        v-if="currentTab === MenuKeys.OVERVIEW"
                        :channel="channel"
                        @change-tab="(key) => (currentTab = key)"
                    />
                    <Audience v-if="currentTab === MenuKeys.AUDIENCE" :channel="channel" />
                    <Performance v-if="currentTab === MenuKeys.PERFORMANCE" :channel="channel" />
                    <CampaignsList
                        v-if="currentTab === MenuKeys.CAMPAIGNS"
                        :channel="channel"
                        @drill-down="onDrillDown"
                    />
                    <AdSetsList
                        v-if="currentTab === MenuKeys.ADSETS"
                        :channel="channel"
                        :campaign-id="filterCampaignId"
                        @drill-down="onDrillDown"
                        @clear-filter="filterCampaignId = null"
                    />
                    <AdsList
                        v-if="currentTab === MenuKeys.ADS"
                        :channel="channel"
                        :adset-id="filterAdsetId"
                        @clear-filter="filterAdsetId = null"
                    />
                    <UpdateDetail v-if="currentTab === MenuKeys.DETAIL" v-model="channel" />
                    <Visibility v-if="currentTab === MenuKeys.VISIBILITY" v-model="channel" />
                </div>
            </div>
        </div>

        <a-modal
            v-model="deleteModal"
            icon="exclamation-circle"
            :title="$t('metaInsights.channelTitle')"
            @cancel="deleteModal = false"
        >
            <p>{{ $t("whatsappChannel.deleteChannel") }}</p>
            <template #footer>
                <a-button key="back" @click="deleteModal = false">
                    {{ $t("chatbotv2.dismiss") }}
                </a-button>
                <a-button key="submit" type="danger" @click="deleteMetaInsightsChannel">
                    {{ $t("chatbotv2.delete") }}
                </a-button>
            </template>
        </a-modal>
    </div>
</template>

<script>
import Vue from "vue";
import _ from "lodash";
import { mapActions, mapState } from "vuex";
import IconButton from "../../../Settings/components/IconButton.vue";
import AppBreadCrumb from "../../../../../components/common/AppBreadCrumb.vue";
import MetaIcon from "../../../Chatbot/pages/integrations/svg/MetaIcon.vue";
import SidebarChild from "../../../../../components/Sidebar/SidebarChild.vue";
import MetaInsightsMenuTypes from "./MetaInsightsMenuTypes";
import Overview from "./Overview.vue";
import Audience from "./Audience.vue";
import Performance from "./Performance.vue";
import CampaignsList from "./CampaignsList.vue";
import AdSetsList from "./AdSetsList.vue";
import AdsList from "./AdsList.vue";
import UpdateDetail from "./UpdateDetail.vue";
import Visibility from "./Visibility.vue";
import log2 from "../../../../../utils/log2";

const log = log2("MetaInsightsChannelDetail.vue");

export default Vue.extend({
    name: "MetaInsightsChannelDetail",
    components: {
        IconButton,
        AppBreadCrumb,
        MetaIcon,
        SidebarChild,
        Overview,
        Audience,
        Performance,
        CampaignsList,
        AdSetsList,
        AdsList,
        UpdateDetail,
        Visibility,
    },
    data() {
        const path = (_.last(this.$route.path.split("/")) || "overview").toLowerCase().trim();
        const validKeys = Object.values(MetaInsightsMenuTypes.MENU_KEYS);
        const startKey = validKeys.indexOf(path) >= 0 ? path : MetaInsightsMenuTypes.MENU_KEYS.OVERVIEW;
        return {
            currentTab: startKey,
            loading: false,
            selectedChannelId: this.$route.params.id,
            deleteModal: false,
            channel: null,
            originalChannel: null,
            MenuKeys: MetaInsightsMenuTypes.MENU_KEYS,
            menuItems: MetaInsightsMenuTypes.Items(this),
            filterCampaignId: this.$route.query.campaignId || null,
            filterAdsetId: this.$route.query.adsetId || null,
        };
    },
    computed: {
        ...mapState({
            siteId: (state) => _.get(state.site.site, "_id"),
            user: (state) => state.auth.user,
        }),
        adAccountName() {
            return _.get(this.channel, "payload.selectedAdAccount.name");
        },
    },
    methods: {
        ...mapActions({
            listChannels: "channel/listChannels",
            deleteChannel: "channel/deleteChannel",
            updateChannel: "channel/updateChannel",
        }),
        deleteMetaInsightsChannel() {
            const channel = this.channel;
            this.deleteChannel(channel._id).then(() => {
                this.$router.push("/integrations/meta-insights");
            });
        },
        handleSave() {
            const payload = this.channel;
            this.loading = true;
            this.updateChannel(payload)
                .then(() => {
                    this.listChannels();
                    this.$message.success(this.$t("message.channelUpdatedSucs"));
                    this.originalChannel = _.cloneDeep(this.channel);
                })
                .catch((err) => {
                    console.error(err);
                    this.$message.error(this.$t("message.failedUpdateChannel"));
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        onDrillDown(payload) {
            if (payload.type === "campaign") {
                this.filterCampaignId = payload.row.id;
                this.filterAdsetId = null;
                this.currentTab = MetaInsightsMenuTypes.MENU_KEYS.ADSETS;
            } else if (payload.type === "adset") {
                this.filterAdsetId = payload.row.id;
                this.currentTab = MetaInsightsMenuTypes.MENU_KEYS.ADS;
            }
        },
    },
    mounted() {
        this.listChannels().then((channels) => {
            this.channel = channels.find((x) => x._id === this.selectedChannelId);
            this.originalChannel = _.cloneDeep(this.channel);
        });
    },
    watch: {
        channel: {
            handler: function (val) {
                if (val) {
                    const isChanged = !_.isEqual(val, this.originalChannel);
                    if (isChanged) {
                        log("channel changed=>", val);
                        this.handleSave();
                    }
                }
            },
            deep: true,
        },
        currentTab(value) {
            const validKeys = Object.values(MetaInsightsMenuTypes.MENU_KEYS);
            if (validKeys.indexOf(value) >= 0 && this.channel) {
                const query = {};
                if (this.filterCampaignId) query.campaignId = this.filterCampaignId;
                if (this.filterAdsetId) query.adsetId = this.filterAdsetId;
                this.$router
                    .push({
                        path: `/integrations/meta-insights/${this.channel._id}/detail/${value}`,
                        query,
                    })
                    .catch(() => {});
            }
        },
        "$route.path": function (newPath) {
            const path = (_.last(newPath.split("/")) || "").toLowerCase().trim();
            const validKeys = Object.values(MetaInsightsMenuTypes.MENU_KEYS);
            if (validKeys.indexOf(path) >= 0 && this.currentTab !== path) {
                this.currentTab = path;
            }
        },
    },
});
</script>

<style scoped lang="scss">
#meta-insights-channel-detail {
    display: flex;
    flex-direction: column;
    background: var(--color-bg-default);
    position: relative;
    width: 100%;
    height: 100%;

    .wrapper {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;

        .toolbar {
            padding: 10px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            height: 50px;
            border: 1px solid var(--color-border-default);

            .left {
                display: flex;
                align-items: center;
                gap: 5px;

                .name {
                    font-size: 18px;
                    font-weight: 700;

                    .ad-account {
                        font-weight: 400;
                        color: var(--color-text-muted-default);
                        font-size: 14px;
                        margin-left: 6px;
                    }
                }

                .icon-container {
                    width: 30px;
                    height: 30px;
                    display: flex;
                    margin: 0 auto;
                    align-items: center;
                    justify-content: center;
                    border-radius: 6px;
                    overflow: hidden;
                    position: relative;
                }
            }

            .right {
                display: flex;
                justify-content: flex-end;
                gap: 5px;
            }
        }

        .channel-content {
            display: flex;
            height: 100%;

            .page-content {
                flex: 1;
                min-width: 0;
                height: 100%;
                overflow-y: auto;
            }
        }
    }
}
</style>
